// stores/deals.ts
import { Deal, Trade } from '@/models/registry';
import {
  assignedTaskStore,
  commentStore,
  confirmationHistoryStore,
  confirmationsStore,
  coRegistrantStore,
  customerAddressStore,
  fileMetadataStore,
  hubStore,
  paymentStore,
  primaryCustomerStore,
  tradeStore,
} from '@/models/stores/index';
import type {
  ArchivedDeal,
  AssignedTask as AssignedTaskType,
  Comment,
  CoreDeal as CoreDealType,
  Customer as CustomerType,
  DealListItem,
  DEALS_GROUPED_BY_DELIVERY_TYPE,
  DEALS_GROUPED_BY_STAGE,
  DEALS_GROUPED_BY_STATUS,
  DeliveryPreference,
  FileMetadata,
  FinanceManager,
  Payment as PaymentType,
  Priority,
  Reply,
  Stage,
} from '@/models/types';

import { UPSERT_CONFIG } from '@/models/stores/archived-deal-upsert-helper.ts';
import { replyStore } from '@/models/stores/replies.ts';
import { taskStoreV2 } from '@/models/stores/tasks-v2.ts';
import dayjs from 'dayjs';
import {
  computed,
  makeAutoObservable,
  observable,
  runInAction,
  toJS,
  type IComputedValue,
  type IObservableArray,
  type ObservableMap,
} from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import { aiDealSuggestions } from './ai/deal-suggestions';
import type { BaseStore } from './base';

const INDEX_KEYS = new Set<keyof CoreDealType>([
  'hub_path_identifier',
  'short_id',
  'archived_at',
]);

// todo: we could generalize the update <property> methods to be more DRY
export class DealStore implements BaseStore<Deal, CoreDealType> {
  // Active deals ID -> deal
  store: ObservableMap<string, Deal> = observable.map();
  // Short ID -> deal
  storeIndexedByShortId: ObservableMap<string, Deal> = observable.map();

  // Hub identifier -> list of deals
  storeIndexedByForeignKey: ObservableMap<string, IObservableArray<Deal>> =
    observable.map();
  cancelledDealsLoaded = false;

  // Archived
  archivedStore: ObservableMap<string, Deal> = observable.map();
  archivedStoreIndexedByShortId: Map<string, Deal> = new Map();
  archivedStoreIndexedByForeignKey: ObservableMap<
    string,
    IObservableArray<Deal>
  > = observable.map();
  filteredArchivedDealsListByShortId: DealListItem[] = [];

  // Shared empty fallback
  private _empty: IObservableArray<Deal> = observable.array([], {
    deep: false,
  });

  // Store IComputedValue<T> and call .get() when reading
  // this improves performance by avoiding unnecessary re-computations
  private _shortIdsByHubComputed = new Map<string, IComputedValue<string[]>>();

  /**
   * DEALS GROUPED BY STATUS
   */
  private _dealsListByStatusComputed = new Map<
    string,
    IComputedValue<DEALS_GROUPED_BY_STATUS>
  >();

  /**
   * DEALS GROUPED BY STAGE
   */
  private _dealsByStageComputed = new Map<
    string,
    IComputedValue<DEALS_GROUPED_BY_STAGE>
  >();

  constructor() {
    makeAutoObservable(this);
  }

  markCancelledDealsLoaded() {
    this.cancelledDealsLoaded = true;
  }

  /**
   * NEW INDEXING STRUCTURE
   */

  private setDealField = <K extends keyof CoreDealType>(
    deal: Deal,
    key: K,
    value: CoreDealType[K],
  ) => {
    (deal as Pick<Deal, K>)[key] = value as Deal[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CoreDealType>;
    hydrateIndexedDB?: boolean;
  }) {
    const isCancelledDelta = delta.data?.status === 'cancelled';
    const existingDeal = this.getById(delta.record_id);
    if (!this.cancelledDealsLoaded && isCancelledDelta && !existingDeal) {
      return;
    }

    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.core_deals.delete(delta.record_id);
        db.cancelled_core_deals.delete(delta.record_id);
      }
      this.removeById(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        if (isCancelledDelta) {
          db.cancelled_core_deals.put(delta.data, delta.record_id);
        } else {
          db.cancelled_core_deals.delete(delta.record_id);
          db.core_deals.put(delta.data, delta.record_id);
        }
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        if (isCancelledDelta) {
          db.cancelled_core_deals.put(delta.data, delta.record_id);
          if (existingDeal) {
            db.core_deals.update(delta.record_id, delta.data);
          }
        } else {
          db.cancelled_core_deals.delete(delta.record_id);
          if (existingDeal?.status === 'cancelled') {
            db.core_deals.put(delta.data, delta.record_id);
          } else {
            db.core_deals.update(delta.record_id, delta.data);
          }
        }
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      if (isCancelledDelta) {
        db.cancelled_core_deals.put(delta.data, delta.record_id);
      } else {
        db.cancelled_core_deals.delete(delta.record_id);
        db.core_deals.put(delta.data, delta.record_id);
      }
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<CoreDealType>;
  }) {
    const existingDeal = this.getById(delta.record_id);
    if (!existingDeal) return;

    const oldHubIdentifier = existingDeal.hub_path_identifier;
    const oldShortId = existingDeal.short_id;
    const wasArchived = !!existingDeal.archived_at;

    let changed = false;
    let indexFieldsChanged = false;

    const entries = Object.entries(delta.data) as [
      keyof CoreDealType,
      CoreDealType[keyof CoreDealType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingDeal[key] === newValue) {
        continue;
      }

      this.setDealField(existingDeal, key, newValue);
      changed = true;

      if (INDEX_KEYS.has(key)) {
        indexFieldsChanged = true;
      }
    }

    if (!changed) return;
    this.save(
      existingDeal,
      indexFieldsChanged
        ? { oldHubIdentifier, oldShortId, wasArchived }
        : undefined,
    );
  }

  private getIndexMap(archived: boolean) {
    return archived
      ? this.archivedStoreIndexedByForeignKey
      : this.storeIndexedByForeignKey;
  }

  private getOrCreateCollection(
    map: ObservableMap<string, IObservableArray<Deal>>,
    key: string,
  ): IObservableArray<Deal> {
    let arr = map.get(key);
    if (!arr) {
      arr = observable.array<Deal>([], { deep: false });
      map.set(key, arr);
    }
    return arr;
  }

  private addToIndexes(deal: Deal) {
    const map = this.getIndexMap(!!deal.archived_at);
    this.getOrCreateCollection(map, deal.hub_path_identifier).push(deal);
  }

  private removeFromIndexes(deal: Deal) {
    const map = this.getIndexMap(!!deal.archived_at);
    const arr = this.getOrCreateCollection(map, deal.hub_path_identifier);
    const i = arr.findIndex((d) => d.id === deal.id);
    if (i >= 0) arr.splice(i, 1);
    if (arr.length === 0) map.delete(deal.hub_path_identifier);
  }

  add(input: CoreDealType) {
    const store = input.archived_at ? this.archivedStore : this.store;
    const storeIndexedByShortId = input.archived_at
      ? this.archivedStoreIndexedByShortId
      : this.storeIndexedByShortId;
    const storeIndexedByForeignKey = input.archived_at
      ? this.archivedStoreIndexedByForeignKey
      : this.storeIndexedByForeignKey;
    const deal = new Deal(input);
    store.set(deal.id, deal);
    storeIndexedByShortId.set(deal.short_id, deal);
    storeIndexedByForeignKey.set(
      deal.hub_path_identifier,
      this.getOrCreateCollection(
        storeIndexedByForeignKey,
        deal.hub_path_identifier,
      ),
    );
    this.addToIndexes(deal);
  }

  replace({ id, deal }: { id: string; deal: CoreDealType }) {
    const existingDeal = this.getById(id);
    if (!existingDeal) return;

    const oldHub = existingDeal.hub_path_identifier;
    const oldShort = existingDeal.short_id;
    const wasArchived = !!existingDeal.archived_at;

    // update the deal fields (models already observable)
    existingDeal.status = deal.status;
    existingDeal.stage = deal.stage;
    existingDeal.hub_path_identifier = deal.hub_path_identifier;
    existingDeal.cdk_id = deal.cdk_id;
    existingDeal.priority = deal.priority;
    existingDeal.pickup_date = deal.pickup_date;
    existingDeal.delivery_date = deal.delivery_date;
    existingDeal.delivery_preference = deal.delivery_preference;
    existingDeal.date_added_to_customer_delivery_queue =
      deal.date_added_to_customer_delivery_queue;
    existingDeal.funding_method = deal.funding_method;
    existingDeal.risk = deal.risk;
    existingDeal.favorited = deal.favorited;
    existingDeal.ai_summary = deal.ai_summary;
    existingDeal.sms_conversation = deal.sms_conversation;
    existingDeal.agent_convo_paused = deal.agent_convo_paused;
    existingDeal.sms_flow = deal.sms_flow;
    existingDeal.sms_flow_step = deal.sms_flow_step;
    existingDeal.primary_sales_rep_id = deal.primary_sales_rep_id;
    existingDeal.secondary_sales_rep_id = deal.secondary_sales_rep_id;
    existingDeal.finance_manager_id = deal.finance_manager_id;
    existingDeal.lender_id = deal.lender_id;
    existingDeal.vehicle_current_location_id = deal.vehicle_current_location_id;
    existingDeal.rapid_recon_step = deal.rapid_recon_step;
    existingDeal.delivery_stage = deal.delivery_stage;
    existingDeal.vehicle_delivery_odometer = deal.vehicle_delivery_odometer;

    existingDeal.vehicle_listing_id = deal.vehicle_listing_id;
    existingDeal.archived_at = deal.archived_at;

    this.save(existingDeal, {
      oldHubIdentifier: oldHub,
      oldShortId: oldShort,
      wasArchived,
    });
  }

  save(
    deal: Deal,
    opts?: {
      oldHubIdentifier?: string;
      oldShortId?: string;
      wasArchived?: boolean;
    },
  ) {
    const isArchived = !!deal.archived_at;

    // primary maps
    const primary = isArchived ? this.archivedStore : this.store;
    const shortMap = isArchived
      ? this.archivedStoreIndexedByShortId
      : this.storeIndexedByShortId;
    primary.set(deal.id, deal);
    shortMap.set(deal.short_id, deal);

    // ensure current hub array contains latest deal reference
    const currArr = this.getOrCreateCollection(
      this.getIndexMap(isArchived),
      deal.hub_path_identifier,
    );
    const idx = currArr.findIndex((d) => d.id === deal.id);
    if (idx >= 0) currArr.splice(idx, 1, deal);
    else currArr.push(deal);

    // if hub changed (or archive state toggled), remove from old index
    if (
      opts?.oldHubIdentifier &&
      (opts.oldHubIdentifier !== deal.hub_path_identifier ||
        (opts.wasArchived ?? isArchived) !== isArchived)
    ) {
      const oldArr = this.getOrCreateCollection(
        this.getIndexMap(opts.wasArchived ?? isArchived),
        opts.oldHubIdentifier,
      );
      const j = oldArr.findIndex((d) => d.id === deal.id);
      if (j >= 0) oldArr.splice(j, 1);
      if (oldArr.length === 0)
        this.getIndexMap(opts.wasArchived ?? isArchived).delete(
          opts.oldHubIdentifier,
        );
    }
  }

  removeById(id: CoreDealType['id']) {
    const deal = this.getById(id);
    if (!deal) return;

    this.store.delete(id);
    this.storeIndexedByShortId.delete(deal.short_id);
    this.removeFromIndexes(deal);
  }

  removeArchivedDealById(id: CoreDealType['id']) {
    const archivedDeal = this.getArchivedDealById(id);
    if (!archivedDeal) return;

    this.archivedStore.delete(id);
    this.archivedStoreIndexedByShortId.delete(archivedDeal.short_id);
    this.removeFromIndexes(archivedDeal);
  }

  /**
   * NEW COMPUTE FUNCTIONS
   */

  private buildShortIdsForHub(hubPathIdentifier: string): string[] {
    const deals =
      this.storeIndexedByForeignKey.get(hubPathIdentifier) || this._empty;
    return deals.map((d) => d.short_id);
  }

  shortIdsByHub(hubPathIdentifier: string): string[] {
    if (!this._shortIdsByHubComputed.has(hubPathIdentifier)) {
      const comp = computed(() => this.buildShortIdsForHub(hubPathIdentifier), {
        keepAlive: true,
      });
      this._shortIdsByHubComputed.set(hubPathIdentifier, comp);
    }
    return this._shortIdsByHubComputed.get(hubPathIdentifier)!.get();
  }

  private buildDealsListByStatus(
    hubPathIdentifier: string,
  ): DEALS_GROUPED_BY_STATUS {
    const result: DEALS_GROUPED_BY_STATUS = {
      draft: { status: 'draft', count: 0, deals: [] },
      new: { status: 'new', count: 0, deals: [] },
      in_progress: { status: 'in_progress', count: 0, deals: [] },
      awaiting_delivery: { status: 'awaiting_delivery', count: 0, deals: [] },
      delivered: { status: 'delivered', count: 0, deals: [] },
      done: { status: 'done', count: 0, deals: [] },
      cancelled: { status: 'cancelled', count: 0, deals: [] },
      returning: { status: 'returning', count: 0, deals: [] },
      returned: { status: 'returned', count: 0, deals: [] },
    };
    const deals =
      this.storeIndexedByForeignKey.get(hubPathIdentifier) || this._empty;
    for (const deal of deals) {
      const item = this.getDealListItem(deal);
      result[deal.status].count++;
      result[deal.status].deals.push(item);
    }
    (Object.keys(result) as (keyof DEALS_GROUPED_BY_STATUS)[]).forEach(
      (status) => {
        const arr = result[status].deals;
        if (arr.length)
          arr.sort((a, b) =>
            dayjs(b.created_at).diff(dayjs(a.created_at), 'date'),
          );
      },
    );
    return result; // no toJS -> stable identity while unchanged
  }

  dealsListByStatus(hubPathIdentifier: string): DEALS_GROUPED_BY_STATUS {
    if (!this._dealsListByStatusComputed.has(hubPathIdentifier)) {
      const comp = computed(
        () => this.buildDealsListByStatus(hubPathIdentifier),
        { keepAlive: true },
      );
      this._dealsListByStatusComputed.set(hubPathIdentifier, comp);
    }
    return this._dealsListByStatusComputed.get(hubPathIdentifier)!.get();
  }

  private buildDealsByStage(hubPathIdentifier: string): DEALS_GROUPED_BY_STAGE {
    const result: DEALS_GROUPED_BY_STAGE = {
      none: { stage: 'none', count: 0, deals: [] },
      pre_contracting: { stage: 'pre_contracting', count: 0, deals: [] },
      finance_call: { stage: 'finance_call', count: 0, deals: [] },
      contracting: { stage: 'contracting', count: 0, deals: [] },
      funding: { stage: 'funding', count: 0, deals: [] },
      funded: { stage: 'funded', count: 0, deals: [] },
    };

    // Read only deals for this hub (O(k)), not this.all (O(n))
    const deals =
      this.storeIndexedByForeignKey.get(hubPathIdentifier) || this._empty;
    for (const deal of deals) {
      const item = this.getDealListItem(deal);
      result[deal.stage].count++;
      result[deal.stage].deals.push(item);
    }

    // Sort in place; no toJS so identity stays stable unless contents change
    (Object.keys(result) as Stage[]).forEach((stage) => {
      const arr = result[stage].deals;
      if (arr.length) {
        arr.sort((a, b) =>
          dayjs(b.created_at).diff(dayjs(a.created_at), 'date'),
        );
      }
    });

    return result;
  }

  dealsByStage(hubPathIdentifier: string): DEALS_GROUPED_BY_STAGE {
    if (!this._dealsByStageComputed.has(hubPathIdentifier)) {
      this._dealsByStageComputed.set(
        hubPathIdentifier,
        computed(() => this.buildDealsByStage(hubPathIdentifier), {
          keepAlive: true,
        }),
      );
    }
    return this._dealsByStageComputed.get(hubPathIdentifier)!.get();
  }

  // Keep old API names but forward to new stable ones (optional)
  getDealsListData({ hubPathIdentifier }: { hubPathIdentifier: string }) {
    return this.dealsListByStatus(hubPathIdentifier);
  }

  getDealsByStageData({ hubPathIdentifier }: { hubPathIdentifier: string }) {
    return this.dealsByStage(hubPathIdentifier);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<CoreDealType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<CoreDealType>;
    hydrateIndexedDB?: boolean;
  }) {
    const isCancelledDelta = delta.data?.status === 'cancelled';
    const existingDeal = this.getById(delta.record_id);
    if (!this.cancelledDealsLoaded && isCancelledDelta && !existingDeal) {
      return;
    }

    if (delta.data.archived_at && delta.action === 'update') {
      hydrateIndexedDB = false;
    }

    if (delta.action === 'update' && !delta.data.archived_at) {
      this.removeArchivedDealById(delta.record_id);
    }

    if (hydrateIndexedDB) {
      if (delta.action === 'delete' || delta.action === 'archive') {
        db.core_deals.delete(delta.record_id);
        db.cancelled_core_deals.delete(delta.record_id);
      } else if (isCancelledDelta) {
        db.cancelled_core_deals.put(delta.data, delta.record_id);
        if (existingDeal) {
          db.core_deals.update(delta.record_id, delta.data);
        }
      } else {
        db.cancelled_core_deals.delete(delta.record_id);
        db.core_deals.put(delta.data, delta.record_id);
      }
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.removeById(delta.record_id);
      db.core_deals.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): Deal | undefined {
    return this.store.get(id) ?? this.archivedStore.get(id);
  }

  getByShortId(shortId: string): Deal | undefined {
    return (
      this.storeIndexedByShortId.get(shortId) ??
      this.archivedStoreIndexedByShortId.get(shortId)
    );
  }

  upsert(deal: CoreDealType) {
    const existingDeal = this.getById(deal.id);
    if (existingDeal) {
      const diff = this.getDiffMap(existingDeal, deal);
      if (diff.size > 0) {
        aiDealSuggestions.removeFromMatchingDeltaPacket(diff);
      }
      this.replace({ id: deal.id, deal });
    } else {
      this.add(deal);
    }
  }

  // for now, we'll only diff properties that are relevant to AI suggestions
  getDiffMap(existingDeal: Deal, deal: CoreDealType): Map<string, string> {
    const diff = new Map<string, string>();

    if (existingDeal.status !== deal.status) {
      diff.set('status', deal.id);
    }

    if (existingDeal.stage !== deal.stage) {
      diff.set('stage', deal.id);
    }

    if (existingDeal.priority !== deal.priority) {
      diff.set('priority', deal.id);
    }
    return diff;
  }

  /**
   * Computed properties
   */
  get all(): Deal[] {
    return Array.from(this.store.values());
  }

  get allNonCancelled(): Deal[] {
    return this.all.filter((deal) => deal.status !== 'cancelled');
  }

  private _getDealsByDeliveryType({
    hubPathIdentifier,
  }: {
    hubPathIdentifier: string;
  }): DEALS_GROUPED_BY_DELIVERY_TYPE {
    // Define the priority order for sorting
    const priorityOrder: Record<Priority, number> = {
      high: 3,
      medium: 2,
      low: 1,
      none: 0,
    };

    // Initialize the result with all possible statuses
    const result: DEALS_GROUPED_BY_DELIVERY_TYPE = {
      none: {
        delivery_preference: 'none',
        count: 0,
        deals: [],
      },
      pickup: {
        delivery_preference: 'pickup',
        count: 0,
        deals: [],
      },
      delivery: {
        delivery_preference: 'delivery',
        count: 0,
        deals: [],
      },
      local_delivery: {
        delivery_preference: 'local_delivery',
        count: 0,
        deals: [],
      },
      extended: {
        delivery_preference: 'extended',
        count: 0,
        deals: [],
      },
    };

    // Process deals for this hub and organize by delivery preference
    const deals =
      this.storeIndexedByForeignKey.get(hubPathIdentifier) || this._empty;
    for (const deal of deals) {
      const dealListItem: DealListItem = this.getDealListItem(deal);
      const deliveryPreference = deal.delivery_preference;

      result[deliveryPreference].count++;
      // TODO (jimmy): fix this type error
      result[deliveryPreference].deals.push(dealListItem as any);
    }

    // Sort by delivery date, then by priority
    for (const deliveryPreference of Object.keys(
      result,
    ) as DeliveryPreference[]) {
      if (result[deliveryPreference].deals.length > 0) {
        result[deliveryPreference].deals.sort((a, b) => {
          const integerDateDiffer = dayjs(a.delivery_date).diff(
            dayjs(b.delivery_date),
            'date',
          );

          if (integerDateDiffer != 0) {
            return integerDateDiffer;
          }

          return priorityOrder[b.priority] - priorityOrder[a.priority];
        });
      }
    }

    return toJS(result);
  }

  get dealsByDeliveryTypeIndexedByHubPathIdentifier(): Record<
    string,
    DEALS_GROUPED_BY_DELIVERY_TYPE
  > {
    const result: Record<string, DEALS_GROUPED_BY_DELIVERY_TYPE> = {};
    const hubPathIdentifiers = hubStore.allHubPathIds;

    for (const hubPathIdentifier of hubPathIdentifiers) {
      result[hubPathIdentifier] = this._getDealsByDeliveryType({
        hubPathIdentifier,
      });
    }

    return result;
  }

  getDealsByDeliveryType({
    hubPathIdentifier,
  }: {
    hubPathIdentifier: string;
  }): DEALS_GROUPED_BY_DELIVERY_TYPE {
    return this.dealsByDeliveryTypeIndexedByHubPathIdentifier[
      hubPathIdentifier
    ];
  }

  getDealsByHubPathId(hubPathId: string): Deal[] {
    return this.storeIndexedByForeignKey.get(hubPathId) || this._empty;
  }

  private getDealListItem(deal: Deal): DealListItem {
    return {
      id: deal.id,
      stage: deal.stage,
      status: deal.status,
      priority: deal.priority,
      short_id: deal.short_id,
      created_at: deal.created_at,
      archived_at: deal.archived_at,
      customer_name: deal.primary_customer?.full_name || '',
      customer_email: deal.primary_customer?.email || null,
      co_registrant_name: deal.co_registrant?.full_name || null,
      finance_manager: deal.finance_manager,
      vehicle: deal.vehicle_listing || null,
      delivery_date: deal.delivery_date || '',
      pickup_date: deal.pickup_date || null,
      date_added_to_customer_delivery_queue:
        deal.date_added_to_customer_delivery_queue || null,
      assigned_tasks: deal.assigned_tasks,
      payments: deal.payments,
      trades: deal.trades,
      primary_sales_rep: deal.primary_sales_rep,
      secondary_sales_rep: deal.secondary_sales_rep,
      delivery_preference: deal.delivery_preference,
      rapid_recon_step: deal.rapid_recon_step,
      delivery_stage: deal.delivery_stage,
      vehicle_delivery_odometer: deal.vehicle_delivery_odometer,
    };
  }

  getActiveDealsByVehicleListingId(vehicleListingId: string): Deal[] {
    return this.all.filter(
      (deal) =>
        deal.vehicle_listing_id === vehicleListingId &&
        deal.status !== 'cancelled' &&
        deal.status !== 'done',
    );
  }

  /**
   * Actions (mobx actions)
   * Properties (status, stage, priority, etc...)
   */

  updateStatus({
    id,
    status,
  }: {
    id: CoreDealType['id'];
    status: CoreDealType['status'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.status = status;
    if (status === 'delivered' || status === 'done') {
      deal.delivery_stage = 'delivered';
    } else if (status === 'awaiting_delivery') {
      deal.delivery_stage = 'pending_pickup';
    } else {
      deal.delivery_stage = 'none';
    }

    this.save(deal);
  }

  updateStage({
    id,
    stage,
  }: {
    id: CoreDealType['id'];
    stage: CoreDealType['stage'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;
    deal.stage = stage;
    this.save(deal);
  }

  updatePriority({
    id,
    priority,
  }: {
    id: CoreDealType['id'];
    priority: CoreDealType['priority'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;
    deal.priority = priority;
    this.save(deal);
  }

  updateCdkId({
    id,
    cdkId,
  }: {
    id: CoreDealType['id'];
    cdkId: CoreDealType['cdk_id'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;
    deal.cdk_id = cdkId;
    this.save(deal);
  }

  updateDeliveryPreference({
    id,
    deliveryPreference,
  }: {
    id: CoreDealType['id'];
    deliveryPreference: CoreDealType['delivery_preference'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.delivery_preference = deliveryPreference;
    this.save(deal);
  }

  updateDeliveryDate({
    id,
    deliveryDate,
  }: {
    id: CoreDealType['id'];
    deliveryDate: CoreDealType['delivery_date'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;
    deal.delivery_date = deliveryDate;
    this.save(deal);
  }

  updatePickupDate({
    id,
    pickupDate,
  }: {
    id: CoreDealType['id'];
    pickupDate: CoreDealType['pickup_date'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.pickup_date = pickupDate;
    this.save(deal);
  }

  updateVehicleDeliveryOdometer({
    id,
    vehicleDeliveryOdometer,
  }: {
    id: CoreDealType['id'];
    vehicleDeliveryOdometer: CoreDealType['vehicle_delivery_odometer'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.vehicle_delivery_odometer = vehicleDeliveryOdometer;
    this.save(deal);
  }

  updateRapidReconStep({
    id,
    rapidReconStep,
  }: {
    id: CoreDealType['id'];
    rapidReconStep: CoreDealType['rapid_recon_step'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.rapid_recon_step = rapidReconStep;
    this.save(deal);
  }

  updateDeliveryStage({
    id,
    deliveryStage,
  }: {
    id: CoreDealType['id'];
    deliveryStage: CoreDealType['delivery_stage'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.delivery_stage = deliveryStage;
    this.save(deal);
  }

  updateFundingMethod({
    id,
    fundingMethod,
  }: {
    id: CoreDealType['id'];
    fundingMethod: CoreDealType['funding_method'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.funding_method = fundingMethod;
    this.save(deal);
  }

  updateSalesReps({
    id,
    salesReps,
  }: {
    id: CoreDealType['id'];
    salesReps: (
      | CoreDealType['primary_sales_rep_id']
      | CoreDealType['secondary_sales_rep_id']
    )[];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.primary_sales_rep_id = salesReps[0];
    deal.secondary_sales_rep_id = salesReps[1];
    this.save(deal);
  }

  addCommentV3({ id, comment }: { id: CoreDealType['id']; comment: Comment }) {
    const deal = this.getById(id);

    if (!deal) return;

    commentStore.add({
      id: comment.id,
      commentable_id: comment.commentable_id,
      commentable_type: comment.commentable_type,
      created_at: comment.created_at,
      updated_at: comment.updated_at,
      user_id: comment.user_id,
      content: comment.content,
      text: comment.text,
      edited: comment.edited,
      replies: comment.replies,
    });
  }

  removeCommentV3({
    id,
    commentId,
  }: {
    id: CoreDealType['id'];
    commentId: string;
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    commentStore.remove(commentId);
  }

  replaceCommentAndDedupeV3({
    idToReplace,
    comment,
    dealId,
  }: {
    idToReplace: string;
    comment: Comment;
    dealId: CoreDealType['id'];
  }) {
    const deal = this.getById(dealId);
    if (!deal) return;

    commentStore.replace({
      id: idToReplace,
      comment,
    });
  }

  replaceReplyAndDedupeV3({
    idToReplace,
    reply,
    dealId,
  }: {
    idToReplace: string;
    reply: Reply;
    dealId: CoreDealType['id'];
  }) {
    const deal = this.getById(dealId);
    if (!deal) return;

    replyStore.replace({
      id: idToReplace,
      reply,
    });
  }

  addReplyV3({ id, reply }: { id: CoreDealType['id']; reply: Reply }) {
    const deal = this.getById(id);
    if (!deal) return;

    replyStore.add({
      id: reply.id,
      comment_id: reply.comment_id,
      created_at: reply.created_at,
      updated_at: reply.updated_at,
      user_id: reply.user_id,
      content: reply.content,
      edited: reply.edited,
      text: reply.text,
      mentions: reply.mentions,
    });
  }

  updateCoRegistrantEmail({
    id,
    email,
  }: {
    id: CoreDealType['id'];
    email: CustomerType['email'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    coRegistrantStore.updateEmail({
      id,
      email,
    });
  }

  updateCoRegistrantFullName({
    id,
    fullName,
  }: {
    id: CoreDealType['id'];
    fullName: CustomerType['full_name'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    coRegistrantStore.updateFullName({
      id,
      fullName,
    });
  }

  updateCoRegistrantPhone({
    id,
    phone,
  }: {
    id: CoreDealType['id'];
    phone: CustomerType['mobile_number'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    coRegistrantStore.updateMobileNumber({
      id,
      mobileNumber: phone,
    });
  }

  addToCustomerDeliveryQueue({
    id,
    dateAdded,
  }: {
    id: CoreDealType['id'];
    dateAdded: CoreDealType['date_added_to_customer_delivery_queue'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.date_added_to_customer_delivery_queue = dateAdded;
    this.save(deal);
  }

  removeFromCustomerDeliveryQueue({ id }: { id: CoreDealType['id'] }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.date_added_to_customer_delivery_queue = null;
    this.save(deal);
  }

  updateFileName({
    id,
    fileName,
    fileId,
  }: {
    id: CoreDealType['id'];
    fileName: FileMetadata['name'];
    fileId: FileMetadata['id'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    fileMetadataStore.updateName(fileId, fileName);
  }

  updateFinanceManager({
    id,
    financeManager,
  }: {
    id: CoreDealType['id'];
    financeManager: FinanceManager | null;
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    if (financeManager) {
      deal.finance_manager_id = financeManager.id;
    } else {
      deal.finance_manager_id = null;
    }

    this.save(deal);
  }

  updatePrimaryCustomerFullName(
    id: CustomerType['id'],
    fullName: CustomerType['full_name'],
  ) {
    primaryCustomerStore.updateFullName({
      id,
      fullName,
    });
  }

  updatePrimaryCustomerEmail(
    id: CustomerType['id'],
    email: CustomerType['email'],
  ) {
    primaryCustomerStore.updateEmail({
      id,
      email,
    });
  }

  updatePrimaryCustomerMobileNumber(
    id: CustomerType['id'],
    mobileNumber: CustomerType['mobile_number'],
  ) {
    primaryCustomerStore.updateMobileNumber({
      id,
      mobileNumber,
    });
  }

  restorePayment(id: PaymentType['id']) {
    paymentStore.restore(id);
  }

  removePayment(id: PaymentType['id'], dealId: PaymentType['deal_id']) {
    paymentStore.remove(id, dealId);
  }

  softDeletePayment(id: PaymentType['id']) {
    paymentStore.softDelete(id);
  }

  upsertPayment(payment: PaymentType) {
    paymentStore.upsert(payment);
  }

  uploadReceiptToPayment(id: PaymentType['id']) {
    paymentStore.uploadReceipt(id);
  }

  upsertAssignedTask({
    id,
    assignedTask,
  }: {
    id: CoreDealType['id'];
    assignedTask: AssignedTaskType;
    taskToReplaceId?: string;
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    assignedTaskStore.upsertAssignedTask({
      assignedTask,
      deal_id: id,
    });
  }

  removeAssignedTask({
    id,
    taskId,
  }: {
    id: CoreDealType['id'];
    taskId: string;
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    assignedTaskStore.remove({ taskId, dealId: id });
  }

  removeTradeIn({
    id,
    tradeId,
  }: {
    id: CoreDealType['id'];
    tradeId: Trade['id'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    tradeStore.remove(tradeId);
  }

  updateLender({
    id,
    lenderId,
  }: {
    id: CoreDealType['id'];
    lenderId: CoreDealType['lender_id'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.lender_id = lenderId;
    this.save(deal);
  }

  updateVehicleCurrentLocation({
    id,
    locationId,
  }: {
    id: CoreDealType['id'];
    locationId: CoreDealType['vehicle_current_location_id'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.vehicle_current_location_id = locationId;
    this.save(deal);
  }

  removeLender({ id }: { id: CoreDealType['id'] }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.lender_id = null;
    this.save(deal);
  }

  updateAgentConvoPaused({
    id,
    agentConvoPaused,
  }: {
    id: CoreDealType['id'];
    agentConvoPaused: CoreDealType['agent_convo_paused'];
  }) {
    const deal = this.getById(id);
    if (!deal) return;

    deal.agent_convo_paused = agentConvoPaused;
    this.save(deal);
  }

  // removeById(id: CoreDealType['id']) {
  //   const deal = this.getById(id);
  //   if (!deal) return;
  //   this.store.delete(id);
  //   this.storeIndexedByShortId.delete(deal.short_id);
  //   const arr = this.storeIndexedByForeignKey.get(deal.hub_identifier) || [];
  //   const filtered = arr.filter((d) => d.id !== id);

  //   if (filtered.length > 0) {
  //     this.storeIndexedByForeignKey.set(deal.hub_identifier, filtered);
  //   } else {
  //     this.storeIndexedByForeignKey.delete(deal.hub_identifier);
  //   }
  // }

  upsertArchivedDealsBulk(deals: ArchivedDeal[]) {
    deals.forEach((deal) => this.upsertArchivedDeal(deal));
  }

  upsertArchivedDeal(archivedDeal: ArchivedDeal) {
    this.upsert(archivedDeal);

    UPSERT_CONFIG.forEach(({ store, extractor }) => {
      const data = extractor(archivedDeal);
      if (Array.isArray(data)) {
        data.forEach((item) => store.upsert(item));
      } else if (data) {
        store.upsert(data);
      }
    });

    this.upsertCustomers(archivedDeal);
    this.upsertComments(archivedDeal);
    this.upsertConfirmations(archivedDeal);
    this.upsertTaskAssignments(archivedDeal);
    this.upsertTasks(archivedDeal);
  }

  private upsertCustomers(deal: ArchivedDeal) {
    deal.customers?.forEach((customer) => {
      const store =
        customer.customer_type === 'co_registrant'
          ? coRegistrantStore
          : primaryCustomerStore;
      store.upsert(customer);
      if (customer.registration_address)
        customerAddressStore.upsert(customer.registration_address);
      if (customer.delivery_address)
        customerAddressStore.upsert(customer.delivery_address);
    });
  }

  private upsertComments(deal: ArchivedDeal) {
    deal.comments?.forEach((comment) => {
      commentStore.upsert(comment);
      comment.replies?.forEach((r) => replyStore.upsert(r));
    });
  }

  private upsertConfirmations(deal: ArchivedDeal) {
    deal.confirmations?.forEach((confirmation) => {
      confirmationsStore.upsert(confirmation);
      confirmation.confirmation_history?.forEach((h) =>
        confirmationHistoryStore.upsert(h),
      );
    });
  }

  private upsertTaskAssignments(deal: ArchivedDeal) {
    deal.deal_task_assignments?.forEach((task) => {
      assignedTaskStore.upsertAssignedTask({
        assignedTask: task,
        deal_id: task.deal_id,
      });
    });
  }

  private upsertTasks(deal: ArchivedDeal) {
    deal.tasks?.forEach((task) => {
      taskStoreV2.upsert(task);
    });
  }

  get archivedDealsForNavigation(): string[] {
    return this.filteredArchivedDealsListByShortId.map((deal) => deal.short_id);
  }

  populateFilteredArchivedDeals(deals: DealListItem[]) {
    this.filteredArchivedDealsListByShortId = deals;
  }

  ingestArchivedDealPage(items: ArchivedDeal[], opts?: { replace?: boolean }) {
    runInAction(() => {
      if (opts?.replace) {
        this.clearAllArchivedDeals();
      }
      for (const item of items) {
        this.upsertArchivedDeal(item);
      }
    });
  }

  clearAllArchivedDeals() {
    runInAction(() => {
      this.archivedStore.clear();
      this.archivedStoreIndexedByShortId.clear();
      this.archivedStoreIndexedByForeignKey.clear();
    });
  }

  getArchivedDealById(id: string): Deal | undefined {
    return this.archivedStore.get(id);
  }

  getArchivedDealByShortId(shortId: string): Deal | undefined {
    return this.archivedStoreIndexedByShortId.get(shortId);
  }

  get allArchivedDeals(): Deal[] {
    return Array.from(this.archivedStore.values());
  }

  // removeArchivedDealById(id: CoreDealType['id']) {
  //   const archivedDeal = this.getArchivedDealById(id);
  //   if (!archivedDeal) return;
  //   this.archivedStore.delete(id);
  //   this.archivedStoreIndexedByShortId.delete(archivedDeal.short_id);
  //   const arr =
  //     this.archivedStoreIndexedByForeignKey.get(archivedDeal.hub_identifier) ||
  //     [];
  //   const filtered = arr.filter((d) => d.id !== id);

  //   if (filtered.length > 0) {
  //     this.archivedStoreIndexedByForeignKey.set(
  //       archivedDeal.hub_identifier,
  //       filtered,
  //     );
  //   } else {
  //     this.archivedStoreIndexedByForeignKey.delete(archivedDeal.hub_identifier);
  //   }
  // }

  getArchivedDealListByHubPathId(hubPathId: string): DealListItem[] {
    return this.getArchivedDealsByHubPathId(hubPathId).map((deal) =>
      this.getDealListItem(deal),
    );
  }

  getAllArchivedDealList(): DealListItem[] {
    return this.allArchivedDeals.map((deal) => this.getDealListItem(deal));
  }

  getArchivedDealsByHubPathId(hubPathId: string): Deal[] {
    return this.archivedStoreIndexedByForeignKey.get(hubPathId) || this._empty;
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'core_deals',
      entity: 'core_deals',
      modelStore: dealStore,
    });
  }

  get activeDeals(): CoreDealType[] {
    const activeStatuses = [
      'draft',
      'new',
      'in_progress',
      'awaiting_delivery',
      'delivered',
    ];
    return Array.from(this.store.values()).filter((deal) =>
      activeStatuses.includes(deal.status),
    );
  }

  async hydrateActiveDeals(): Promise<void> {
    const activeStatuses = [
      'draft',
      'new',
      'in_progress',
      'awaiting_delivery',
      'delivered',
    ];
    const activeDealsIds = await db.core_deals
      .toArray()
      .then((deals) =>
        deals
          .filter((deal) => activeStatuses.includes(deal.status))
          .map((deal) => deal.id),
      );

    await hydrateFromWorker({
      tableName: 'core_deals',
      entity: 'core_deals',
      modelStore: dealStore,
      ids: activeDealsIds,
    });
  }

  async hydrateDeal(shortId: CoreDealType['short_id']): Promise<void> {
    const deal = await db.core_deals.get({ short_id: shortId });
    if (deal) this.upsert(deal);
  }

  get newDeals(): CoreDealType[] {
    return Array.from(this.store.values()).filter(
      (deal) => deal.status === 'new',
    );
  }
}

export const dealStore = new DealStore();
